import React, { useEffect, useRef, useCallback } from "react";
import { horizontalLoop } from "@/core/utils/horizontal-helper-gsap";
import { useTickerContext } from "./TickerContext";

const TICKER_ITEMS = [
  "🔥 New: Premium Flower in stock!",
  "💨 Vape Cartridges - Smooth & Potent",
  "🍬 Edibles: Delicious & Effective",
  "📍 Visit us at 3400 Connecticut Ave NW, DC",
  "🎉 Daily Deals & Promotions!",
  "🌱 DC's Premier Cannabis Dispensary",
];

const TICKER_SPEED = 0.27;
const MIN_GAP = 100; // Minimum gap between items in pixels
const SAFETY_MULTIPLIER = 3; // Create extra copies to prevent gaps

const HorizontalTickerHomePage: React.FC = () => {
  const tickerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { tickerAnimation, isTickerInitialized, setTickerInitialized } =
    useTickerContext();

  // Initialize ticker with proper spacing and overlap prevention
  const initializeTicker = useCallback(() => {
    if (!tickerRef.current || isTickerInitialized) return;

    const items = tickerRef.current.querySelectorAll(".ticker-item");
    if (items.length === 0) return;

    // Wait for DOM to be fully ready and styles to be applied
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // Clean up any existing animation
          if (tickerAnimation.current) {
            tickerAnimation.current.kill();
            tickerAnimation.current = null;
          }

          // Ensure items are properly spaced before animation
          items.forEach((item, index) => {
            const element = item as HTMLElement;
            element.style.marginRight = `${MIN_GAP}px`;
            element.style.flexShrink = "0";
            element.style.whiteSpace = "nowrap";
          });

          // Initialize the horizontal loop with safer settings
          tickerAnimation.current = horizontalLoop(items, {
            speed: TICKER_SPEED,
            repeat: -1,
            paused: false,
            snap: false,
            paddingRight: MIN_GAP * 2, // Extra padding to prevent overlap
          });

          setTickerInitialized(true);
          console.log("✅ Ticker initialized successfully");
        } catch (error) {
          console.error("❌ Ticker initialization failed:", error);
          // Retry initialization after a brief delay
          setTimeout(() => {
            setTickerInitialized(false);
          }, 1000);
        }
      });
    });
  }, [isTickerInitialized, setTickerInitialized, tickerAnimation]);

  // Handle resize events to maintain proper spacing
  useEffect(() => {
    const handleResize = () => {
      if (tickerAnimation.current && isTickerInitialized) {
        // Pause, recalculate, and resume
        tickerAnimation.current.pause();

        requestAnimationFrame(() => {
          if (tickerAnimation.current) {
            tickerAnimation.current.play();
          }
        });
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [isTickerInitialized, tickerAnimation]);

  // Initialize ticker when component mounts or when DOM is ready
  useEffect(() => {
    // Multiple initialization attempts to handle different loading states
    const initTimer1 = setTimeout(initializeTicker, 100);
    const initTimer2 = setTimeout(initializeTicker, 500);
    const initTimer3 = setTimeout(initializeTicker, 1000);

    return () => {
      clearTimeout(initTimer1);
      clearTimeout(initTimer2);
      clearTimeout(initTimer3);
    };
  }, [initializeTicker]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tickerAnimation.current) {
        tickerAnimation.current.kill();
        tickerAnimation.current = null;
      }
    };
  }, [tickerAnimation]);

  // Create enough copies to prevent gaps and overlaps
  const tickerItemsWithCopies = Array(SAFETY_MULTIPLIER)
    .fill(TICKER_ITEMS)
    .flat();

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden ticker-container"
      style={{
        position: "relative",
        height: "32px", // Fixed height to prevent layout shifts
      }}
    >
      <div
        ref={tickerRef}
        className="flex items-center"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: "100%",
          minWidth: "200%", // Ensure enough width for seamless loop
        }}
      >
        {tickerItemsWithCopies.map((text, idx) => (
          <span
            key={`ticker-${idx}`}
            className="ticker-item text-law-black font-semibold text-xs md:text-sm whitespace-nowrap flex-shrink-0"
            style={{
              marginRight: `${MIN_GAP}px`,
              paddingLeft: "16px",
              paddingRight: "16px",
              display: "inline-block",
              minWidth: "max-content",
            }}
          >
            {text}
          </span>
        ))}
      </div>
    </div>
  );
};

export default React.memo(HorizontalTickerHomePage);
